import React from "react";
import { LegalLayout } from "@/components/site/LegalLayout";
import { COMPANY } from "@/lib/constants";

const CANONICAL = "https://alldaypowerwashing.com/contract";

export default function ContractPage() {
  return (
    <LegalLayout
      title="Customer Contract"
      subtitle="Service agreement"
      description="The Customer Contract that governs All Day Power Washing's residential and commercial exterior cleaning services."
      canonical={CANONICAL}
      testId="contract-page"
    >
      <p className="text-slate-400 text-sm">Last updated: February 2026</p>

      <p>
        This Customer Contract (the "Contract"), by and between{" "}
        <strong>{COMPANY.name}</strong> ("Company") and Client, sometimes
        referred to herein collectively as the "Parties", is made effective the
        moment that Client accepts Company's proposal or estimate.
      </p>
      <p>
        WHEREAS, Company wishes for Client to understand Company's expectations
        in exchange for the services Company provides;
      </p>
      <p>
        WHEREAS, Company wishes to provide residential and commercial exterior
        cleaning services, such as pressure washing, soft washing, roof
        cleaning, window cleaning, deck cleaning, and gutter cleaning services
        to Client in accordance with the terms of this Contract.
      </p>
      <p>
        WHEREAS, Client wishes for Company to perform the aforementioned
        services at Client's property in accordance with the terms of this
        Contract.
      </p>
      <p>
        NOW, THEREFORE, for good and valuable consideration, including but not
        limited to the consideration described below, the receipt and
        sufficiency of which are hereby acknowledged, the Parties agree as
        follows:
      </p>

      <h2>1. Acceptance of Terms</h2>
      <p>
        Client understands that by accepting an estimate provided by Company,
        Client agrees to <strong>all</strong> of the terms and conditions
        contained in this Contract. Client authorizes Company to accomplish the
        job specified in the estimate. Client further agrees to hold Company
        harmless for any property damage not caused by Company's negligence.
        Company is not responsible for damages already noted during
        pre-inspection, when providing an estimate, or during on-site pre-work
        inspection, including but not limited to, damages to{" "}
        <strong>loose</strong> siding, paint, wood, trim, windows, oxidation,
        landscaping, or roofing.
      </p>

      <h2>2. Binding Agreement</h2>
      <p>
        This Contract serves as a binding agreement between Client and Company.
        This Contract constitutes the sole and entire agreement between the
        parties hereto and no modification of this Contract shall be binding
        unless signed by all parties or assigns to this Contract. No
        representation, promise, or inducement not included in this Contract
        shall be binding on any party hereto.
      </p>

      <h2>3. Authorizations</h2>
      <p>
        Client shall allow Company, its employees, and representatives access
        to Client's property to accomplish the requested cleaning services.
        Client further agrees to allow Company to visit the property prior to
        the date of service for assessment and after the services were
        performed for a follow-up check. The aforementioned visits may be done
        with short or no notice.
      </p>
      <p>
        On the date of service, Client agrees to allow Company to use the
        residential/commercial water source via an outdoor spigot. In areas
        where well water is used or there is low water pressure, Client agrees
        to provide advance notice of such conditions so Company can prepare and
        bring an adequate amount of water for the cleaning service. In the
        event Company is required to supplement the water supply, Client
        understands that additional charges will be assessed to Client as extra
        equipment is needed to transport water and feed the water from the
        tanks. These additional fees will vary depending on factors like
        location and the quantity of water needed.
      </p>
      <p>
        Company equipment is powered by Company's systems, so Company will not
        connect to Client's home's electrical supply. Company's crew members
        will inspect Client's spigot to ensure that there is no damage in it
        and the area surrounding. Company's crew members will take before and
        after photographs documenting the condition of the spigot. Client
        agrees that Company shall not be held liable for either previously
        damaged spigot issues or issues outside Client's home unrelated to
        cleaning services.
      </p>
      <p>
        Company cannot proceed with any washing or cleaning service if Client
        has ongoing construction and/or home improvement projects going on.
        Client is hereby advised to schedule Company's services to be performed
        on dates when no construction and/or home improvement projects are
        being completed. A cancellation fee of <strong>$150.00</strong> shall
        be charged to Client if Company arrives at Client's property for
        washing and/or cleanings and construction and/or home improvement
        projects are ongoing.
      </p>
      <p>
        During unavoidable circumstances, such as emergency or inclement
        weather conditions, Company shall perform the requested cleaning
        services on the next available business day. Client acknowledges that
        as much as Company wants to accommodate a quick reschedule, Company has
        other considerations to take into account such as working with other
        scheduled clients and weather situations.
      </p>

      <h2>4. Payment Terms</h2>
      <p>
        Unless otherwise agreed by the parties in writing, payment is due upon
        the completion of work. Company accepts all forms of payment including
        cash, check, or credit. If paying with credit, Company accepts payment
        through a credit card processor link or the office for the privacy and
        security of Company's information.
      </p>
      <p>
        Any invoice or scheduled payment over 10 days past due will be subject
        to a 7% late fee. If any invoice or scheduled payment goes 30 days past
        due, Company shall have the right to file a civil suit to collect the
        outstanding debt. Company further reserves the right to refuse to
        continue to do business with a client who is currently past due or who
        has been 30+ days past due more than once.
      </p>
      <p>
        If Client and Company have a net 30 days' payment schedule, Client must
        have payment submitted to Company within a 30-day grace period. Time is
        of the essence. Any invoice or scheduled payment that is 30 days past
        due after the date of the Contract will be subject to a 7% late fee. If
        payment is not received within 60 days of the due date, Company
        reserves the right to file a civil suit to collect the outstanding
        debt. Company further reserves the right to refuse to continue to do
        business with a client under a payment schedule who is currently past
        due or who has been 60+ days past due more than once.
      </p>

      <h2>5. Risks and Releases of Liability</h2>
      <p>
        Company has expert technicians who operate the equipment used in any
        cleaning service. Company always takes extra precautions not only to
        keep Company's technicians safe but also to avoid causing any damage to
        Client's property. Company understands the type of pressure that a
        surface needs when cleaning, like the use of low pressure on delicate
        surfaces. However, damage can be inevitable due to various reasons like
        poor maintenance, neglect, or low-grade building materials. Client is
        advised to implement routine maintenance on the home's surface and
        also ensure a watertight seal to the home before the date of cleaning
        to avoid damage. Company shall not be held liable for any damages that
        occur due to improper maintenance, neglect, or low-grade building
        materials.
      </p>
      <p>
        On the date of the cleaning service, Company will note any pre-existing
        damage to the area to be serviced using a checklist sheet that will be
        signed by Client. In cases when Client cannot sign, Company will note
        the absence and take photos of the noted damages. Client or the
        homeowner should also sign the checklist sheet as proof that they have
        been informed of any damage. If Client is not present, Company's
        employee in charge of the service will notify Client of the noted
        damages with supporting documents. If Company found any new damage,
        the services will stop until Client can see the damage and acknowledge
        its existence.
      </p>
      <p>
        Company has a set of procedures to protect plant life in the area.
        Client understands that cleaning or washing is scheduled during midday
        sun, and there is a possibility of leaf burn, as water can get around
        the plant during the cleaning procedures. Any issues with plant life
        within the area of washing will be evaluated by Company to determine
        the plan of action. It is Client's responsibility to know what plants
        on the property are suffering from leaf burn or are wilted upon
        arrival. Company is not liable for any damages to plants or landscape
        that were burned prior to Company showing up, as Company takes high
        precaution in treating landscape with proper care.
      </p>

      <h2>6. House Wash Acknowledgment</h2>
      <p>
        Client must have a water spigot available, or arrangements should have
        been made with Company for water that will be brought to the site,
        subject to additional fees, as outlined in Section 3 of this Contract.
        Client is hereby advised that Company's service equipment needs a
        direct connection to a water line and not on a water faucet. Client
        understands that any existing oxidation, flaws, and blemishes will be
        more visible after cleaning. Vinyl siding that lacks maintenance and
        exposed to the sun is susceptible to oxidation. Oxidation can cause a
        chalky, white powder on the surface, thus eliminating the clear luster.
        The effects of oxidation can be noticeable only after cleaning, as it
        can be covered with dirt and debris. This should not be pointed out as
        Company's fault after cleaning, as it is always discussed during
        pre-inspection and the cleaning process unless it is due to the
        company's negligence. Client should shut off all electric outlets and
        fixtures outside before the arrival of Company's team as a precaution.
      </p>
      <p>
        In case of "site unseen estimates" where Company uses only photos
        provided by Client, Eagle view software, Google maps, or other locally
        listed information, Company cannot take note of any flaws in exterior
        surfaces. However, Company will do its best to point out any flaws in
        exterior surfaces on the date of cleaning. Client understands that
        Company uses a cleaning solution that is specifically formulated for
        their cleaning services. It is proven not to cause any discoloration or
        damage to home siding. Client should not be alarmed if Client notices
        the faint rusty color on Client's siding. These are called weep holes
        to allow ventilation and allow condensation to drain out. However,
        these can be little homes for bugs. Debris and dirt can also accumulate
        in these holes. After washing, as it goes to the drying process,
        leftover water or bug juices drained from these holes. These can be bug
        feces, mold, and debris. These will come off with the rain, morning
        dew, or garden hose. These are not stains and are not permanent. If the
        weep holes drip heavily, Company agrees to do a visual inspection and a
        full rinse.
      </p>
      <p>
        Client understands that during house wash service, Client will also
        receive basic window cleaning. This is different from the window
        cleaning done with a purified water pole system. The basic cleaning
        will remove dirt and debris off windows but can leave water spots.
        Company suggests removing window screens for a more effective wash, but
        does not guarantee water spots or "hazing" will not occur. For thorough
        cleaning, Client is suggested to use Company's more advanced window
        cleaning services. This uses filtered water to be sure that nothing in
        the water can leave streaks and water spots. Client understands that
        the window screen needs to be removed before wash. Window screens can
        be brittle and delicate, which is why Company will not remove them
        unless a consent form is signed by Client. Company will not be liable
        for worn and brittle screens.
      </p>

      <h2>7. Roof Treatment Acknowledgment</h2>
      <p>
        Client must have a water spigot available, or arrangements should have
        been made with Company for water that will be brought to the site,
        subject to additional fees, as outlined in Section 3 of this Contract.
        Client is hereby advised that Company's service equipment needs a
        direct connection to a water line and not on a water faucet. Client
        understands that Company uses a cleaning solution that is guaranteed to
        kill damaging growths like algae, moss, or lichen. Roofs react
        differently to the solution, meaning the result may vary. There are
        times when it takes more time to completely remove the dead growths.
        The materials and the age of the roof, the location, and the amount of
        build-up can affect the length of time to see the results. Company does
        not pull growth with a force, as it can cause damage. However, if
        Client insists on Company doing so, Client should sign a waiver stating
        that Company is not liable for any damage that it may cause and that
        Client understands that doing so could void the warranty with the roof
        manufacturer. Client understands that roofs naturally suffer granular
        loss over time. Algae, moss, and lichen can cause more significant
        granular loss. Also, after a roof cleaning, areas affected by granular
        loss are more visible since no organic matter is covering it.
      </p>

      <h2>8. Touch Ups on Roofs</h2>
      <p>
        Touch-ups on roofs may be needed after washing. There is a possibility
        that washing may leave light brown areas after treatment and washing.
        These are just dead algae, or the black streaks before treatment. In
        these cases, Client must understand that Client needs to wait for dead
        algae to come off with the help of natural elements like rain and sun
        exposure. These traces will fade after 6 to 8 weeks or longer,
        especially on shingles. If the traces do not fade after 6 to 8 weeks,
        Client can contact Company so the parties can assess and decide what
        to do next. The span of time can also determine if the reapplication of
        the solution is needed. Touch-ups are not charged to Client given that
        it is necessary for proper treatment on the initial quote.
      </p>

      <h2>9. Client's Obligations on Date of Service</h2>
      <p>
        Client hereby agrees to ensure that the following preparations take
        place prior to Company's arrival on the date of service:
      </p>
      <ul>
        <li>Doors and windows should be shut tightly.</li>
        <li>Please have all pet droppings removed from areas immediately around home.</li>
        <li>Shut off all outside electrical outlets and fixtures at the breaker on the scheduled date even before the team arrives.</li>
        <li>Remove window screens for better washing and rinsing.</li>
        <li>Keep pets and kids indoors before and during cleaning.</li>
        <li>Clear the area to be serviced and remove sensitive materials like rags, doormats, outdoor furniture, and your vehicles from the areas being washed.</li>
        <li>Client should refrain from using other water outlets in the residence to maintain the pressure or volume that is needed in cleaning.</li>
        <li>Have an accessible and activated water spigot, or other arrangements should have been made with Company for water that will be brought to the site, subject to additional fees, as outlined in Section 3 of this Contract.</li>
      </ul>
      <p>
        Client is hereby advised that Company's service equipment needs a
        direct connection to a water line and not on a water faucet. For
        maximum pressure, Company should connect to a direct line and not on
        the faucet. Company needs a steady pressure of 40 PSI. Company has a
        set of procedures to cover electrical outlets. However, it is more
        applicable for the client to shut off the electrical supply of all
        exterior outlets. As Company will clean windows, Client should be the
        one to remove window screens, as it can be brittle and fragile. This
        will allow Company to give a thorough cleaning to windows, leaving no
        streaks and marks if Client has purchased the window cleaning package.
      </p>

      <h2>10. Exclusions &amp; Limitations</h2>
      <p>
        To secure a cleaning service, a <strong>20%–30% deposit</strong> is
        required. This amount is non-refundable and will be deducted from the
        total quoted price once the service is completed on Client's invoice.
        The deposit must be paid to keep Client's scheduled slot in connection
        with approving the quote sent via signature. As an example, if a job
        is quoted at $300.00, a $75.00 deposit is required, leaving the
        remaining $225.00 balance due after service. There are some cases when
        a higher deposit is required depending on the material of the surface
        to be clean and the type of cleaning that should be done. This will be
        prepared for Client with details from Company's office manager.
      </p>
      <p>
        A <strong>$55.00</strong> fee will be assessed if the client does not
        have a stable water source ready when the technicians have already
        arrived. The water pressure should always be consistent at 40 PSI.
        This cannot be maintained if there is water failure and will then
        affect the cleaning process. An example of water failure is when the
        filtration system fails or the well pump stops and Company is not the
        one at fault. If water source can be a problem, Client must have other
        options or must make some arrangements with Company for Company to
        bring water to the site, which requires additional fees, as outlined
        in Section 3 of this Contract.
      </p>
      <p>
        A cancellation fee of <strong>$150.00</strong> will be assessed if
        Client cancels service without prior notice 72 hours or more before
        the date of service. The same applies when Company has already
        arrived on the scheduled date and cleaning cannot proceed because of
        ongoing construction work.
      </p>
      <p>
        A fee of <strong>$100.00</strong> will be assessed if Company returns
        to the site to review a noted issue or complaint by Client and Company
        finds that the issue was not caused by Company's negligence.
      </p>
      <p>
        For window cleaning service using a purified water-fed pole system,
        Client understands that Client is the one to remove the screen prior
        to the date of the cleaning service. If not, a <strong>$100.00</strong>{" "}
        fee will be assessed. Client further agrees that Company is not liable
        for damaged screens, as screen removal needs prior arrangements with
        Company.
      </p>
      <p>
        A <strong>$45.00</strong> processing fee will be assessed in the case
        of returned checks.
      </p>
      <p>
        Company will not be held responsible for water leaks and intrusion. As
        much as Company makes every effort, there are times when it is
        inevitable due to inadequate seals on windows and doors or cracks on
        walls and foundations.
      </p>
      <p>
        Client understands that Company should be notified in advance in
        writing if there are particular surfaces in the area or its
        surrounding that are sensitive to Company's cleaning solution. If not,
        Company is not responsible for any ill-effect that it may cause on any
        surface.
      </p>
      <p>Client understands that Company is not responsible for the following:</p>
      <ul>
        <li>Unreachable areas beyond 28 ft ladder capability</li>
        <li>Wasp nests and bird nests that are unreachable</li>
        <li>Cleaning in the form of a "stripping" service</li>
        <li>Oxidation removal from gutters, vinyl, metal (tiger stripes)</li>
        <li>Storm windows and leakage</li>
        <li>Uncovered outlets and old outlets that have dry rotted and become unsealed</li>
        <li>Furniture removal and reinstall</li>
        <li>Runoff cleaning (chemical cleans and area that is nasty underneath the area being contracted to clean)</li>
        <li>Weep holes and dirt buildup from behind a weep hole causing runs</li>
        <li>Vine remnant and removal</li>
        <li>Artillery fungus</li>
        <li>Paint splatters — we cannot remove these</li>
        <li>Tree sap</li>
        <li>Wood stripping or Client request to put high pressure on wood</li>
        <li>Open windows</li>
        <li>Well-water problems</li>
        <li>Window spotting as a result of the house washing process</li>
        <li>Paint runs and fading — it is Client's responsibility to understand the type of paint on the home. Some types of paint are notorious for runs and fading with standard house wash procedures, and Company is not liable for paint runs or fading with these types of paint or any organic paint runs or fading.</li>
        <li>Faulty electrical outlet covers allowing water to seep in and enter the outlet, causing fires or the breaker to throw.</li>
        <li>Rare fogging issue with multi-pane windows with bad seals.</li>
      </ul>
      <p>
        This Contract does not guarantee that all stains will be 100% removed.
        Company strives to make the expectations clear, and Company will not
        use pressure as a means of removing a blight if damage would ensue.
        Company is not responsible for any ill effects to any substrates that
        were not pre-discussed beforehand with an email in our inbox. If
        Company has not been notified in advance in writing that something
        cannot have detergent upon it, Company is not responsible for it.
        Company is not responsible for any water intrusion. Company will do
        its best to mitigate any water intrusion, but Client acknowledges that
        bad seals around windows, doors, and concrete can make this
        unavoidable.
      </p>

      <h2>11. Content Use &amp; Release</h2>
      <p>
        Client agrees to give Company permission to use photos, videos,
        reviews, or descriptions of the property for the purpose of
        advertising. These will be used without any compensation to Client.
        Client agrees not to initiate any civil action against Company with
        regard to the use of the above media. Company will not reveal personal
        and/or sensitive information like names and/or addresses. Upon
        Client's approval, Company is allowed to display a sign for marketing
        on the property for no more than seven days. If Client damages the
        signage, Client will be charged the retail cost of the sign.
      </p>

      <h2>12. Company's Damages Liability</h2>
      <p>
        Company is liable for any damage to properties that is a direct result
        of the company's negligence like operator errors and willful
        misconduct. Damages must be reported to the company not more than two
        days after the completion of the cleaning service, otherwise those
        damages are waived. Client agrees to allow Company 30 calendar days
        from the date of being notified of any potential damages to inspect
        and cure the issue(s) prior to Client initiating any action related to
        the alleged damages.
      </p>

      <h2>13. Severability</h2>
      <p>
        If any provision of this Contract or the application thereof is held
        invalid by a court, arbitrator, or government agency of competent
        jurisdiction, the Parties agree that such a determination of
        invalidity shall not affect other provisions or applications of the
        Contract which can be given effect without the invalid provisions and
        thus shall remain in full force and effect or application.
      </p>

      <h2>14. Attorneys' Fees and Costs</h2>
      <p>
        If at any point either Party breaches the commitments contained within
        this Contract, the non-breaching Party is entitled to recover all of
        its reasonable attorneys' fees and all other reasonable costs incurred
        in the preparation and service of any notice or demand hereunder,
        whether or not a legal action is subsequently commenced.
      </p>

      <h2>15. No Waiver of Breach</h2>
      <p>
        The failure to enforce any provision of this Contract shall not be
        construed as a waiver of any such provision, nor prevent a Party
        thereafter from enforcing the provision or any other provision of this
        Contract. The rights granted the Parties are cumulative, and the
        election of one shall not constitute a waiver of such Party's right to
        assert all other legal and equitable remedies available under the
        circumstances.
      </p>

      <h2>16. Choice of Law</h2>
      <p>
        The validity and construction of this Contract shall be determined
        under Colorado law, without regard to its internal conflicts of laws
        rules.
      </p>

      <h2>17. Amendments to the Contract</h2>
      <p>
        This Contract shall not be altered, amended, or modified by oral
        representation made before or after the execution of this Contract.
        All amendments or changes of any kind must be in writing, executed by
        all Parties.
      </p>

      <h2>18. Forum Selection; Jurisdiction; Venue</h2>
      <p>
        For purposes of any action or proceeding arising out of this Contract,
        the parties hereto expressly submit to the jurisdiction of all federal
        and state courts located in El Paso County, Colorado. Further,
        Parties waive and agree not to assert in any such action, suit or
        proceeding that they are not personally subject to the jurisdiction of
        such courts, that the action, suit or proceeding is brought in an
        inconvenient forum or that venue of the action, suit, or proceeding is
        improper.
      </p>

      <p className="mt-10">
        IN WITNESS WHEREOF, this Contract is made effective the moment that
        Client accepts Company's proposal or estimate.
      </p>
      <p className="text-slate-400 text-sm mt-6">
        For questions about this Contract, email{" "}
        <a href={`mailto:${COMPANY.email}`} className="text-[#1E88E5] hover:text-[#42A5F5]">
          {COMPANY.email}
        </a>{" "}
        or call/text {COMPANY.phone}.
      </p>
    </LegalLayout>
  );
}
